// Library Methods
import axios from "axios";

// Utils
import {
  gatewayConfig,
  apiGatewayUrl,
} from "src/utils/services/servicesHelper";

// Types
import {
  EMRHubType,
  EMRHubConnectionSourceKind,
  EMRHubConnectionDirection,
  UnassignedEMRJobsType,
  EMRConnectionType,
  EMRJobType,
  EMRProblemType,
  EMRExportType,
  EMRExportCountsType,
  EMRWorklistDayType,
  EMRExportState,
  EMRDeviceCatalogType,
  EMRPacServerType,
  EMRPacServerJobPayload,
} from "./types";

const getEMRHubs = async (
  accessToken: string,
  clinicId: string
): Promise<Array<EMRHubType>> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRHubById = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<EMRHubType> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const createEMRHub = async (
  accessToken: string,
  clinicId: string,
  hubData: { alias: string }
): Promise<Array<any>> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/emr/portal/hubs`,
      hubData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const updateEMRHub = async (
  accessToken: string,
  clinicId: string,
  hubId: string,
  hubData: { alias: string }
): Promise<Array<any>> => {
  try {
    const { data } = await axios.put(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}`,
      hubData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const deleteEMRHub = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/deactivate`,
      null,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRHubConnections = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<Array<EMRConnectionType>> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/connections`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const createEMRHubConnection = async (
  accessToken: string,
  clinicId: string,
  hubId: string,
  connectionData: {
    clinicId: string;
    name: string;
    sourceKind: EMRHubConnectionSourceKind;
    direction: EMRHubConnectionDirection;
    configJson: Record<string, unknown>;
    enabled: boolean;
    deviceKey?: null | string;
  }
): Promise<EMRConnectionType> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/connections`,
      connectionData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const updateEMRHubConnection = async (
  accessToken: string,
  clinicId: string,
  connectionId: string,
  connectionData: {
    clinicId: string;
    name: string;
    sourceKind: EMRHubConnectionSourceKind;
    direction: EMRHubConnectionDirection;
    configJson: Record<string, unknown>;
    enabled: boolean;
    deviceKey?: null | string;
  }
): Promise<EMRConnectionType> => {
  try {
    const { data } = await axios.put(
      `${apiGatewayUrl}/emr/portal/connections/${connectionId}`,
      connectionData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const updateEMRHubClinics = async (
  accessToken: string,
  clinicId: string,
  hubId: string,
  clinicIds: Array<string>
): Promise<EMRHubType> => {
  try {
    const { data } = await axios.put(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/clinics`,
      { clinicIds },
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRHubProblems = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<Array<EMRProblemType>> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/problems`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRExports = async (
  accessToken: string,
  clinicId: string,
  connectionId: string,
  states: Array<EMRExportState>,
  limit: number
): Promise<Array<EMRExportType>> => {
  try {
    const query = new URLSearchParams({ connectionId, limit: String(limit) });
    states.forEach((state) => query.append("status", state));

    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/exports?${query.toString()}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRExportCounts = async (
  accessToken: string,
  clinicId: string,
  connectionId: string
): Promise<EMRExportCountsType> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/exports/counts?connectionId=${connectionId}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRWorklistDay = async (
  accessToken: string,
  clinicId: string,
  connectionId: string,
  date: string
): Promise<EMRWorklistDayType> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/connections/${connectionId}/worklist-day?date=${date}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const retryEMRExport = async (
  accessToken: string,
  clinicId: string,
  exportId: string
): Promise<EMRExportType> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/emr/portal/exports/${exportId}/retry`,
      null,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const deleteEMRHubConnection = async (
  accessToken: string,
  clinicId: string,
  connectionId: string
): Promise<{ connectionId: string; enabled: boolean; deletedAt: string }> => {
  try {
    const { data } = await axios.delete(
      `${apiGatewayUrl}/emr/portal/connections/${connectionId}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRDeviceCatalog = async (
  accessToken: string,
  clinicId: string
): Promise<EMRDeviceCatalogType> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/catalog/devices`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRPacServers = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<Array<EMRPacServerType>> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/pac-servers`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const createEMRPacServer = async (
  accessToken: string,
  clinicId: string,
  hubId: string,
  pacServerData: {
    clinicId: string;
    name: string;
    deviceKey: string;
    jobs: Array<EMRPacServerJobPayload>;
  }
): Promise<EMRPacServerType> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/pac-servers`,
      pacServerData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const updateEMRPacServer = async (
  accessToken: string,
  clinicId: string,
  pacServerId: string,
  pacServerData: {
    name: string;
    deviceKey: string;
    jobs: Array<EMRPacServerJobPayload>;
  }
): Promise<EMRPacServerType> => {
  try {
    const { data } = await axios.put(
      `${apiGatewayUrl}/emr/portal/pac-servers/${pacServerId}`,
      pacServerData,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const deleteEMRPacServer = async (
  accessToken: string,
  clinicId: string,
  pacServerId: string
): Promise<void> => {
  try {
    await axios.delete(
      `${apiGatewayUrl}/emr/portal/pac-servers/${pacServerId}`,
      gatewayConfig(accessToken, clinicId)
    );
  } catch (error) {
    throw error;
  }
};

const getUnassignedEMR = async (
  accessToken: string,
  clinicId: string
): Promise<UnassignedEMRJobsType> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/clinics/${clinicId}/emr-jobs/unassigned`,
      gatewayConfig(accessToken, clinicId)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteUnassignedEMR = async (
  accessToken: string,
  clinicId: string,
  emrJobId: string
): Promise<{ data: null; message: string; state: boolean }> => {
  try {
    const { data } = await axios.delete(
      `${apiGatewayUrl}/clinics/${clinicId}/emr-jobs/${emrJobId}`,
      gatewayConfig(accessToken, clinicId)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const assignEMRPatient = async (
  accessToken: string,
  clinicId: string,
  emrJobId: string,
  patientData: { patientId: string }
): Promise<{ data: null; message: string; state: boolean }> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/clinics/${clinicId}/emr-jobs/${emrJobId}/assign-patient`,
      patientData,
      gatewayConfig(accessToken, clinicId)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const confirmEMRPatient = async (
  accessToken: string,
  clinicId: string,
  emrJobId: string
): Promise<{ data: null; message: string; state: boolean }> => {
  try {
    const { data } = await axios.post(
      `${apiGatewayUrl}/clinics/${clinicId}/emr-jobs/${emrJobId}/confirm-patient`,
      null,
      gatewayConfig(accessToken, clinicId)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getEMRJobs = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<Array<EMRJobType>> => {
  try {
    const { data } = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/jobs`,
      gatewayConfig(accessToken, clinicId)
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getEMRInstaller = async (
  accessToken: string,
  clinicId: string,
  hubId: string
): Promise<{ blob: Blob; fileName: string | undefined }> => {
  try {
    const response = await axios.get(
      `${apiGatewayUrl}/emr/portal/hubs/${hubId}/installer`,
      { ...gatewayConfig(accessToken, clinicId), responseType: "blob" }
    );

    const contentDisposition = response.headers["content-disposition"];
    const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);

    return { blob: response.data, fileName: fileNameMatch?.[1] };
  } catch (error) {
    throw error;
  }
};

export {
  getEMRHubs,
  getEMRHubById,
  createEMRHub,
  deleteEMRHub,
  updateEMRHub,
  getEMRHubConnections,
  createEMRHubConnection,
  updateEMRHubConnection,
  deleteEMRHubConnection,
  updateEMRHubClinics,
  getUnassignedEMR,
  deleteUnassignedEMR,
  assignEMRPatient,
  confirmEMRPatient,
  getEMRJobs,
  getEMRInstaller,
  getEMRHubProblems,
  getEMRExports,
  getEMRExportCounts,
  getEMRWorklistDay,
  retryEMRExport,
  getEMRDeviceCatalog,
  getEMRPacServers,
  createEMRPacServer,
  updateEMRPacServer,
  deleteEMRPacServer,
};
